import React, { useState, useEffect } from 'react';
import { RefreshCw, CalendarDays, Store } from 'lucide-react';
import { FilterOptions } from '../../../types/analytics';
import { supabaseClient } from '../../utils/supabaseClient';

interface AnalysisFiltersProps {
  filters: FilterOptions;
  onFilterChange: (filters: Partial<FilterOptions>) => void;
  onRefresh: () => void;
  isLoading: boolean;
  lastUpdated: Date;
  userEmail: string;
}

const AnalysisFilters: React.FC<AnalysisFiltersProps> = ({
  filters,
  onFilterChange,
  onRefresh,
  isLoading,
  lastUpdated,
  userEmail
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [stores, setStores] = useState<{ shopname: string }[]>([]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('stores')
          .select('shopname')
          .eq('user_email', userEmail);
        
        if (error) {
          console.error("Error fetching stores:", error.message);
          setStores([]);
          return;
        }

        if (data && data.length > 0) {
          setStores(data);
        } else {
          setStores([]);
        }
      } catch (error) {
        console.error('Error in fetchStores:', error);
        setStores([]);
      }
    };
    
    fetchStores();
  }, [userEmail]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const dateRangePresets = [
    { label: '今日', getRange: () => {
      const today = new Date();
      return { start: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), end: today };
    }},
    { label: '今週', getRange: () => {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() + 1); // 日曜始まり+1
      return { start, end: today };
    }},
    { label: '今月', getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1 + 1);
      return { start, end: today };
    }},
    { label: '先月', getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1 + 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start, end };
    }},
    { label: '過去3ヶ月', getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate() + 1);
      return { start, end: today };
    }},
    { label: '過去6ヶ月', getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate() + 1);
      return { start, end: today };
    }},
    { label: '過去1年', getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() + 1);
      return { start, end: today };
    }},
    { label: '全期間', getRange: () => {
      const start = new Date(2000, 0, 1);
      const today = new Date();
      return { start, end: today };
    }}
  ];

  const handleDateRangeSelect = (preset: { label: string; getRange: () => { start: Date; end: Date } }) => {
    const range = preset.getRange();
    onFilterChange({ dateRange: range, periodLabel: preset.label });
    setShowDatePicker(false);
  };

  const handleStoreSelect = (storeId: string) => {
    // セッションストレージに選択された店舗を保存
    sessionStorage.setItem('selectedStore', storeId);
    onFilterChange({ stores: [storeId] });
    setShowStoreSelector(false);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      <div className="relative">
        <button
          onClick={() => setShowDatePicker(!showDatePicker)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <CalendarDays className="w-4 h-4 text-gray-500" />
          <span>
            {filters.periodLabel}
          </span>
        </button>
        
        {showDatePicker && (
          <div className="absolute top-full mt-1 left-0 z-10 w-64 bg-white border border-gray-200 rounded-md shadow-lg p-3">
            <div className="space-y-2">
              {dateRangePresets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handleDateRangeSelect(preset)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="relative">
        <button
          onClick={() => setShowStoreSelector(!showStoreSelector)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Store className="w-4 h-4 text-gray-500" />
          <span>
            {filters.stores[0]}
          </span>
        </button>
        
        {showStoreSelector && (
          <div className="absolute top-full mt-1 left-0 z-10 w-48 bg-white border border-gray-200 rounded-md shadow-lg p-3">
            <div className="space-y-2">
              {stores.map((store) => (
                <button
                  key={store.shopname}
                  onClick={() => handleStoreSelect(store.shopname)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                >
                  {store.shopname}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 ml-auto"
      >
        <RefreshCw className={`w-4 h-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
        <span>{isLoading ? '更新中...' : '更新'}</span>
      </button>
      
      <div className="text-xs text-gray-500">
        最終更新: {formatDate(lastUpdated)} {formatTime(lastUpdated)}
      </div>
    </div>
  );
};

export default AnalysisFilters;